import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Inbox, Check, X, AlertCircle, CalendarDays, Pill, Clock, Filter, RefreshCw,
} from "lucide-react";

const KIND_META = {
  reschedule_appointment: { icon: CalendarDays, color: "text-brand-700 bg-brand-50 border-brand-200", label: "Reschedule" },
  side_effect_concern:    { icon: AlertCircle,  color: "text-rose-700 bg-rose-50 border-rose-200",     label: "Side-effect" },
  refill_request:         { icon: Pill,         color: "text-amber-700 bg-amber-50 border-amber-200",  label: "Refill" },
  pause_cycle:            { icon: Clock,        color: "text-purple-700 bg-purple-50 border-purple-200", label: "Pause cycle" },
  other:                  { icon: Inbox,        color: "text-stone-700 bg-stone-50 border-stone-200",  label: "Other" },
};

const STATUS_META = {
  pending:  { color: "bg-amber-100 text-amber-800",   label: "Pending" },
  accepted: { color: "bg-emerald-100 text-emerald-800", label: "Accepted" },
  denied:   { color: "bg-rose-100 text-rose-800",     label: "Denied" },
};

export default function ProviderRequestsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get(`/provider/requests${filter ? `?status_filter=${filter}` : ""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]);

  const decide = async (req, decision) => {
    const apply = decision === "accepted" && req.kind === "reschedule_appointment" && !!req.new_date;
    const note = decision === "denied" ? window.prompt("Optional note for the patient (denial reason):", "") : null;
    if (decision === "denied" && note === null) return; // user cancelled prompt
    setBusyId(req.id);
    try {
      await api.patch(`/provider/requests/${req.id}/decision`, {
        decision,
        note: note || undefined,
        apply_change: apply,
      });
      await load();
    } finally { setBusyId(null); }
  };

  const counts = useMemo(() => ({
    pending: rows.filter((r) => r.status === "pending").length,
    accepted: rows.filter((r) => r.status === "accepted").length,
    denied: rows.filter((r) => r.status === "denied").length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="provider-requests-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Patient Requests</h1>
          <p className="text-stone-500 mt-1">Treatment-change requests from the mobile app.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm" data-testid="refresh-requests">
          <RefreshCw className="w-4 h-4" />Refresh
        </button>
      </header>

      <div className="flex items-center gap-2 text-sm">
        <Filter className="w-4 h-4 text-stone-500" />
        {[
          { id: "pending",  label: `Pending (${counts.pending})` },
          { id: "accepted", label: `Accepted (${counts.accepted})` },
          { id: "denied",   label: `Denied (${counts.denied})` },
          { id: "",         label: "All" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id || "all"}`}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${filter === f.id ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-soft overflow-hidden">
        {rows.length === 0 && (
          <div className="p-10 text-center text-stone-500 text-sm" data-testid="empty-requests">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-stone-300" />
            No requests {filter ? `with status "${filter}"` : "yet"}.
          </div>
        )}
        <div className="divide-y divide-stone-100">
          {rows.map((req) => {
            const kind = KIND_META[req.kind] || KIND_META.other;
            const status = STATUS_META[req.status] || { color: "bg-stone-100 text-stone-700", label: req.status };
            const KindIcon = kind.icon;
            return (
              <div key={req.id} className="p-5" data-testid={`request-row-${req.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${kind.color}`}>
                    <KindIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${status.color}`}>{status.label}</span>
                      <span className="font-semibold text-stone-900">{req.patient_name || "Unknown patient"}</span>
                      <span className="text-stone-500 text-sm">· {req.kind_label}</span>
                      <span className="text-stone-400 text-xs ml-auto">{new Date(req.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-stone-700 mt-1.5 text-sm">{req.message}</p>

                    {/* FROM → TO pill for reschedule requests — large + highlighted so the
                        provider sees both dates clearly at a glance. */}
                    {req.kind === "reschedule_appointment" && (req.old_date || req.new_date) && (
                      <div className="mt-3 inline-flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-lg px-4 py-2.5" data-testid={`from-to-${req.id}`}>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">From</div>
                          <div className="font-mono font-bold text-stone-900 text-base leading-tight" data-testid={`from-date-${req.id}`}>{req.old_date || "—"}</div>
                        </div>
                        <svg className="w-5 h-5 text-stone-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 0 1 1.414 0l6 6a1 1 0 0 1 0 1.414l-6 6a1 1 0 0 1-1.414-1.414L14.586 11H3a1 1 0 1 1 0-2h11.586l-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd"/></svg>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">To</div>
                          <div className="font-mono font-bold text-emerald-700 text-base leading-tight" data-testid={`to-date-${req.id}`}>{req.new_date || "—"}</div>
                        </div>
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-stone-500">
                      {req.kind !== "reschedule_appointment" && req.new_date && <span><b>New date:</b> {req.new_date}</span>}
                      {req.severity && <span><b>Severity:</b> {req.severity}</span>}
                      {req.regimen_name && <span><b>Regimen:</b> {req.regimen_name}{req.cycle_number ? ` · Cycle ${req.cycle_number}` : ""}</span>}
                      {req.event_label && req.kind === "reschedule_appointment" && <span><b>Appointment:</b> {req.event_label}{req.event_time ? ` · ${req.event_time}` : ""}</span>}
                      {req.event_id && <span className="font-mono text-stone-400">event {req.event_id.slice(0, 8)}</span>}
                    </div>
                    {req.provider_note && (
                      <div className="mt-2 text-xs text-stone-500 italic">Your note: "{req.provider_note}"</div>
                    )}
                    {req.applied && <div className="mt-1.5 text-xs text-emerald-700 font-semibold">✓ Calendar change applied</div>}
                  </div>
                  {req.status === "pending" && (
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => decide(req, "accepted")} disabled={busyId === req.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold disabled:opacity-60"
                        data-testid={`accept-${req.id}`}>
                        <Check className="w-3.5 h-3.5" />Accept
                      </button>
                      <button onClick={() => decide(req, "denied")} disabled={busyId === req.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold disabled:opacity-60"
                        data-testid={`deny-${req.id}`}>
                        <X className="w-3.5 h-3.5" />Deny
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
